'use client';

import { CSSProperties, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styles from './SideCtaSidebar.module.css';

interface BoundedFixedPanelProps {
  /** Gap (px) between the bottom of the navbar and the top of the panel. */
  navbarGap?: number;
  /** Gap (px) between the bottom of the panel and the top of the footer. */
  footerGap?: number;
  containerMaxWidth?: number;
  children: ReactNode;
}

function getNavbarBottom(): number {
  if (typeof document === 'undefined') return 0;
  // The site navbar is a sticky <header>. Fall back to nav if not found.
  const nav = document.querySelector<HTMLElement>('header') || document.querySelector<HTMLElement>('nav');
  if (!nav) return 0;
  return nav.getBoundingClientRect().bottom;
}

function getContentAnchorTop(): number | null {
  if (typeof document === 'undefined') return null;
  const anchor = document.querySelector<HTMLElement>('[data-side-cta-anchor]');
  if (!anchor) return null;
  return anchor.getBoundingClientRect().top;
}

function getFooterTop(): number {
  if (typeof document === 'undefined') return Number.POSITIVE_INFINITY;
  const footer = document.querySelector<HTMLElement>('footer');
  if (!footer) return Number.POSITIVE_INFINITY;
  return footer.getBoundingClientRect().top;
}

export function BoundedFixedPanel({
  navbarGap = 80,
  footerGap = 24,
  containerMaxWidth = 1200,
  children,
}: BoundedFixedPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState<number>(120);
  const [hidden, setHidden] = useState<boolean>(false);

  useEffect(() => {
    const update = () => {
      const panel = panelRef.current;
      if (!panel) return;

      const panelHeight = panel.offsetHeight;
      const navbarBottom = getNavbarBottom();
      const navbarFloor = navbarBottom + 16; // panel must never cross the navbar

      // If the page tags a content anchor (e.g. the article / hero start),
      // align the panel's top to that element so it lines up with where
      // the content visually begins. Fall back to navbar + navbarGap.
      const anchorTop = getContentAnchorTop();
      const desiredTop =
        anchorTop !== null
          ? Math.max(anchorTop, navbarFloor)
          : Math.max(navbarBottom + navbarGap, navbarGap);

      const footerTop = getFooterTop();
      const viewportH = window.innerHeight;
      const upperBound = Math.min(footerTop, viewportH);

      // If the panel can't fit above the footer, pull it upward.
      const maxTopForFit = upperBound - panelHeight - footerGap;
      const nextTop = Math.min(desiredTop, maxTopForFit);
      setTop(nextTop);
      setHidden(nextTop + panelHeight < 0);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    let ro: ResizeObserver | undefined;
    if (panelRef.current && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(panelRef.current);
    }

    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      ro?.disconnect();
    };
  }, [navbarGap, footerGap]);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const panelHeight = panel.offsetHeight;
    const navbarBottom = getNavbarBottom();
    const navbarFloor = navbarBottom + 16;
    const anchorTop = getContentAnchorTop();
    const desiredTop =
      anchorTop !== null
        ? Math.max(anchorTop, navbarFloor)
        : Math.max(navbarBottom + navbarGap, navbarGap);
    const footerTop = getFooterTop();
    const viewportH = window.innerHeight;
    const upperBound = Math.min(footerTop, viewportH);
    const maxTopForFit = upperBound - panelHeight - footerGap;
    const nextTop = Math.min(desiredTop, maxTopForFit);
    setTop(nextTop);
    setHidden(nextTop + panelHeight < 0);
  }, [navbarGap, footerGap]);

  const style: CSSProperties = {
    top: `${top}px`,
    visibility: hidden ? 'hidden' : 'visible',
    ['--side-cta-container-max' as string]: `${containerMaxWidth}px`,
  };

  return (
    <div ref={panelRef} className={styles.stickyInner} style={style}>
      {children}
    </div>
  );
}
